// Custom PWA install banner.
// Captures `beforeinstallprompt`, defers the native prompt, and shows a
// Discord-style banner at the top of the viewport until the user either
// installs or dismisses. State is remembered in localStorage so the banner
// doesn't re-surface on every visit.
//
// Fires automatically on import of this module (app.ts imports once at
// startup). Safari iOS has no `beforeinstallprompt` event — no banner shows
// there; users add-to-home-screen via the share sheet manually.

interface BeforeInstallPromptEvent extends Event {
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt(): Promise<void>;
}

const STATE_KEY = 'hub-people-install-banner-state';
const BANNER_ID = 'hp-install-banner';

let deferredPrompt: BeforeInstallPromptEvent | null = null;

function bannerDismissed(): boolean {
  try { return !!localStorage.getItem(STATE_KEY); } catch { return false; }
}

function markDismissed(value: 'dismissed' | 'installed'): void {
  try { localStorage.setItem(STATE_KEY, value); } catch { /* private mode */ }
}

function hideBanner(): void {
  document.getElementById(BANNER_ID)?.remove();
}

function showBanner(): void {
  if (bannerDismissed()) return;
  if (document.getElementById(BANNER_ID)) return;

  const banner = document.createElement('div');
  banner.id = BANNER_ID;
  banner.className = 'hp-install-banner';
  banner.setAttribute('role', 'dialog');
  banner.setAttribute('aria-label', 'Install Hub People');
  banner.innerHTML = `
    <div class="hp-install-banner-icon" aria-hidden="true">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
    </div>
    <div class="hp-install-banner-text">
      <strong>Install Hub People</strong>
      <span>Full-screen app experience with notifications.</span>
    </div>
    <button class="hp-install-banner-btn hp-install-banner-install" type="button">Install</button>
    <button class="hp-install-banner-btn hp-install-banner-close" type="button" aria-label="Dismiss">Not now</button>
  `;
  document.body.appendChild(banner);

  banner.querySelector('.hp-install-banner-close')?.addEventListener('click', () => {
    markDismissed('dismissed');
    hideBanner();
  });

  banner.querySelector('.hp-install-banner-install')?.addEventListener('click', () => {
    const prompt = deferredPrompt;
    if (!prompt) { hideBanner(); return; }
    // prompt() MUST be called synchronously from a user gesture — no awaits
    // before this line. userChoice is a promise we can await after.
    void prompt.prompt();
    void prompt.userChoice.then((choice) => {
      if (choice.outcome === 'accepted') markDismissed('installed');
      else markDismissed('dismissed');
      deferredPrompt = null;
      hideBanner();
    });
  });
}

export function initInstallBanner(): void {
  // Already running as installed PWA → never show.
  if (window.matchMedia('(display-mode: standalone)').matches) return;

  // Clear the 'installed' flag if we're in a browser tab — Chrome won't
  // fire beforeinstallprompt once the app is installed on the device, but
  // if the user uninstalls or visits from a new profile the banner should
  // be available again. Only 'dismissed' (user clicked "Not now") persists.
  try {
    if (localStorage.getItem(STATE_KEY) === 'installed') {
      localStorage.removeItem(STATE_KEY);
    }
  } catch { /* private mode */ }

  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e as BeforeInstallPromptEvent;
    showBanner();
  });

  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    hideBanner();
  });
}
